import { useState, useMemo } from 'react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Input, Field } from '@/components/ui/Input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Select } from '@/components/ui/Select';
import { Slider } from '@/components/ui/Slider';

const fmtINR = (v: number) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(v);

export function TaxCalculator() {
  return (
    <div>
      <ToolHeader
        toolId="tax-calculator"
        title="Tax calculator"
        description="GST math (inclusive/exclusive) and India income-tax estimate (FY 2025-26 new regime)."
      />
      <Tabs defaultValue="gst">
        <TabsList className="mb-4">
          <TabsTrigger value="gst">GST</TabsTrigger>
          <TabsTrigger value="income">Income tax (India)</TabsTrigger>
        </TabsList>
        <TabsContent value="gst">
          <GstPanel />
        </TabsContent>
        <TabsContent value="income">
          <IncomePanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function GstPanel() {
  const [amount, setAmount] = useState(10000);
  const [rate, setRate] = useState(18);
  const [mode, setMode] = useState<'add' | 'extract'>('add');

  const result = useMemo(() => {
    if (mode === 'add') {
      const tax = (amount * rate) / 100;
      return { base: amount, tax, total: amount + tax };
    }
    const base = amount / (1 + rate / 100);
    return { base, tax: amount - base, total: amount };
  }, [amount, rate, mode]);

  const cgst = result.tax / 2;
  const sgst = result.tax / 2;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setMode('add')}
            className={`px-3 py-2 text-sm rounded-md border transition ${
              mode === 'add' ? 'border-accent bg-accent/10 text-accent' : 'border-border'
            }`}
          >
            Add GST to net
          </button>
          <button
            onClick={() => setMode('extract')}
            className={`px-3 py-2 text-sm rounded-md border transition ${
              mode === 'extract' ? 'border-accent bg-accent/10 text-accent' : 'border-border'
            }`}
          >
            Extract from gross
          </button>
        </div>
        <Field label={mode === 'add' ? 'Net amount (₹)' : 'Gross amount (₹)'}>
          <Input
            type="number"
            value={amount}
            onChange={(e) => setAmount(Math.max(0, Number(e.target.value) || 0))}
            className="font-mono"
          />
        </Field>
        <Field label="GST rate (%)">
          <Select
            value={String(rate)}
            onChange={(e) => setRate(Number(e.target.value))}
            options={[
              { value: '0', label: '0% (exempt)' },
              { value: '5', label: '5%' },
              { value: '12', label: '12%' },
              { value: '18', label: '18% — most services' },
              { value: '28', label: '28% — luxury' },
            ]}
          />
        </Field>
      </Card>

      <Card>
        <div className="text-2xs uppercase tracking-wider text-fg-subtle">Total payable</div>
        <div className="text-4xl font-semibold mt-2 font-mono tabular-nums">
          {fmtINR(result.total)}
        </div>
        <div className="grid grid-cols-2 gap-2 mt-4">
          <Stat label="Base" value={fmtINR(result.base)} />
          <Stat label="GST" value={fmtINR(result.tax)} highlight />
        </div>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <Stat label={`CGST (${rate / 2}%)`} value={fmtINR(cgst)} />
          <Stat label={`SGST (${rate / 2}%)`} value={fmtINR(sgst)} />
        </div>
        <div className="text-2xs text-fg-subtle mt-3 leading-relaxed">
          Inter-state transactions use IGST at the full rate instead of CGST + SGST.
        </div>
      </Card>
    </div>
  );
}

interface Slab {
  upto: number;
  rate: number;
}

const NEW_REGIME: Slab[] = [
  { upto: 300_000, rate: 0 },
  { upto: 700_000, rate: 5 },
  { upto: 1_000_000, rate: 10 },
  { upto: 1_200_000, rate: 15 },
  { upto: 1_500_000, rate: 20 },
  { upto: Infinity, rate: 30 },
];

function calcSlabs(income: number): { tax: number; breakdown: { range: string; rate: number; tax: number }[] } {
  let tax = 0;
  const breakdown: { range: string; rate: number; tax: number }[] = [];
  let prev = 0;
  for (const slab of NEW_REGIME) {
    const cap = Math.min(slab.upto, income);
    if (cap <= prev) break;
    const taxable = cap - prev;
    const slabTax = (taxable * slab.rate) / 100;
    tax += slabTax;
    breakdown.push({
      range: `${fmtINR(prev)} – ${slab.upto === Infinity ? 'above' : fmtINR(slab.upto)}`,
      rate: slab.rate,
      tax: slabTax,
    });
    prev = slab.upto;
  }
  // Section 87A rebate (income up to 7L, max 25k under new regime)
  if (income <= 700_000) tax = 0;
  return { tax, breakdown };
}

function IncomePanel() {
  const [income, setIncome] = useState(1_500_000);
  const [standardDed, setStandardDed] = useState(75_000);

  const taxable = Math.max(0, income - standardDed);
  const result = useMemo(() => calcSlabs(taxable), [taxable]);
  const cess = result.tax * 0.04;
  const total = result.tax + cess;
  const effective = income > 0 ? (total / income) * 100 : 0;
  const takehome = income - total;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="space-y-4">
        <Field label="Annual income (₹)" hint={fmtINR(income)}>
          <Input
            type="number"
            value={income}
            onChange={(e) => setIncome(Math.max(0, Number(e.target.value) || 0))}
            className="font-mono"
          />
          <Slider
            value={income}
            onChange={setIncome}
            min={300_000}
            max={5_000_000}
            step={50_000}
            showValue={false}
            className="mt-3"
          />
        </Field>
        <Field label="Standard deduction (salaried)" hint={fmtINR(standardDed)}>
          <Input
            type="number"
            value={standardDed}
            onChange={(e) => setStandardDed(Math.max(0, Number(e.target.value) || 0))}
            className="font-mono"
          />
        </Field>
        <div className="text-2xs text-fg-subtle leading-relaxed border-t border-border pt-3">
          Computed under FY 2025-26 new tax regime. Section 87A rebate zeroes tax for incomes up to ₹7L. Cess of 4% applies on top.
        </div>
      </Card>

      <Card>
        <div className="text-2xs uppercase tracking-wider text-fg-subtle">Total tax payable</div>
        <div className="text-4xl font-semibold mt-2 font-mono tabular-nums">{fmtINR(total)}</div>
        <div className="grid grid-cols-2 gap-2 mt-4">
          <Stat label="Take-home" value={fmtINR(takehome)} />
          <Stat label="Effective rate" value={`${effective.toFixed(2)}%`} highlight />
        </div>
        <div className="border-t border-border pt-3 mt-3">
          <div className="text-2xs uppercase tracking-wider text-fg-subtle mb-2">Slab breakdown</div>
          <div className="space-y-1 text-xs font-mono">
            {result.breakdown.map((b, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-fg-muted">{b.range}</span>
                <span className="text-fg-subtle">{b.rate}%</span>
                <span className="tabular-nums w-24 text-right">{fmtINR(b.tax)}</span>
              </div>
            ))}
            <div className="flex items-center justify-between border-t border-border pt-1 mt-1">
              <span className="text-fg-muted">Health & education cess</span>
              <span className="text-fg-subtle">4%</span>
              <span className="tabular-nums w-24 text-right">{fmtINR(cess)}</span>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div
      className={`p-3 rounded-lg border ${
        highlight ? 'bg-warning/10 border-warning/20 text-warning' : 'bg-bg-inset border-border'
      }`}
    >
      <div className="text-2xs uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-sm font-mono font-semibold mt-1 tabular-nums">{value}</div>
    </div>
  );
}

export default TaxCalculator;
